export interface Employee {
    id: string;
    fullname?: string;
    email?: string;
    contact_number?: string;
    nric?: string;
    address?: string;
    formatted_address?: string;
    location_lat?: string;
    location_lng?: string;
    street_number?: string;
    street?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    country_code?: string;
    place_id?: string;
    remarks?: string;
    status?: string;
    gender?: string;
    updated_at?: string;
    created_at?: string;
    blacklisted_at?: string;
    cert?: string;
    type?: 'parttime' | 'fulltime' | 'contractor';
    nationality?: string;
    id_type?: string;
    passport_expiry?: string;
    upload_at?: string;
    cert_type?: string;
    request_doc?: string;
    comment?: string;
    cert_expired?: string;
    // Manager permission related fields
    requires_permission?: boolean;
    first_manager_id?: string;
    first_manager_name?: string;
    permission_status?:
        | 'required'
        | 'self_managed'
        | 'not_required'
        | 'approved';
    image?: string;
    duplicate_ic?: string;
    certificates?: EmployeeCertificate[];
    // Checking workflow fields
    is_checked?: boolean;
    checked_at?: string;
    checked_by?: string;
}

export interface EmployeeCertificate {
    /** Unique identifier for existing certificates from backend */
    id?: string;
    /** Certificate type ID from certificate-management system */
    certificate_type_id: string;
    /** Display name of certificate type */
    certificate_type_name?: string;
    /** Alternative name field from backend (same as certificate_type_name) */
    name?: string;
    /** Alternative cert_type field from backend (same as certificate_type_name) */
    cert_type?: string;
    /**
     * Certificate file - can be:
     * - File object for new uploads
     * - string URL for existing certificates from backend
     */
    file: File | string;
    /** Original filename of the uploaded certificate */
    filename?: string;
    /** Whether this certificate type has an expiry date */
    has_expiry: boolean;
    /** Alternative has_validity field from backend (same as has_expiry) */
    has_validity?: boolean;
    /**
     * Expiry date of the certificate
     * - For new certificates: ISO date string or Date object
     * - For existing certificates: timestamp in milliseconds from backend
     */
    expiry_date?: string;
    /**
     * Certificate expiry timestamp from backend (Unix timestamp in milliseconds as string)
     * Alternative to expiry_date field
     */
    cert_expired?: string | null;
    /**
     * Number of days until certificate expiry
     * - Positive: days remaining until expiry
     * - Negative: days since expiry (expired certificates)
     * - Null: certificate type has no expiry tracking
     */
    days_to_expiry?: number | null;
    /**
     * Current validity status of the certificate
     * - true: certificate is currently valid (not expired)
     * - false: certificate has expired
     */
    is_valid?: boolean;
    /** Timestamp when certificate was uploaded */
    uploaded_at?: string;
    /** URL to existing certificate file (for existing certificates from backend) */
    certificate_url?: string;
    /** Optional description field */
    description?: string | null;
}

export const CERTIFICATE_FILE_CONSTRAINTS = {
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    maxCertificates: 20,
};

export interface EmployeeListPaginationInterface {
    total_records?: number;
    current_page?: number;
    records?: Employee[];
    total_pages?: number;
    page_size?: number;
}

export interface HrDashboard {
    pendingApplicants: string;
    totalActiveEmployees: string;
    totalActiveFulltime: string;
    totalActiveParttimer: string;
    totalActiveContractors: string;
    lastWeekNewActiveParttimer: string;
    lastWeekNewActiveContractor: string;
    totalDischargeParttimer: string;
    contractsValid: string;
    contractsExpired: string;
    foodCertsValid: string;
    foodCertsExpired: string;
}

export interface ExpiredItem {
    id: number;
    fullname: string;
    role: 'fulltime' | 'parttime' | 'contractor';
    contractStatus: 'Active' | 'Expired' | '-';
    dateSigned: string | null;
    contractExpiry: string | null;
    foodCertStatus: 'Active' | 'Expired' | '-';
    foodCertExpiry: string | null;
    managerId: number | null;
    managerName: string | null;
}

export interface ExpiredItemsListPaginationInterface {
    list: ExpiredItem[];
    total: number;
    pageNum: number;
    pageSize: number;
    pages: number;
}

export interface HrShiftAdjustment {
    id: string;
    shift_id?: string;
    employee_id?: string;
    employee_name?: string;
    project_name?: string;
    location_name?: string;
    shift_date?: string;
    planned_start_time?: string;
    planned_end_time?: string;
    actual_start_time?: string;
    actual_end_time?: string;
    original_amount?: string;
    adjusted_amount?: string;
    difference_amount?: string;
    status?: string;
    adjustment_type?: string;
    adjustment_reason?: string;
    requested_date?: string;
    requested_by?: string;
    requested_name?: string;
    approved_date?: string;
    approved_by?: string;
    rejection_reason?: string;
    finance_status?: string;
    finance_approved_date?: string;
    finance_approved?: string;
    finance_reject?: string;
    remarks?: string;
    adjustment_paid_date?: string;
    adjustment_paid_by?: string;
    adjustment_payment_reference?: string;
    updated_at?: string;
    created_at?: string;
}

export interface HrShiftAdjustmentPaginationInterface {
    total_records?: number;
    current_page?: number;
    records?: HrShiftAdjustment[];
    total_pages?: number;
    page_size?: number;
}
